import { differenceInCalendarDays, startOfDay } from "date-fns";
import { RefObject } from "react";
import { normalizeDateOnly } from "./date";

/**
 * Scrolls a container horizontally to a specific left position.
 */
export function scrollToPosition({
  container,
  left,
  top,
  behavior = "auto",
}: {
  container: HTMLElement | null;
  left?: number;
  top?: number;
  behavior?: ScrollBehavior;
}) {
  if (!container) return;
  const nextLeft = left ?? container.scrollLeft;
  const nextTop = top ?? container.scrollTop;
  container.scrollTo({ left: nextLeft, top: nextTop, behavior });
}

/**
 * Scrolls to horizontally center a specific column index.
 */
export function scrollToCenter({
  container,
  centerIdx,
  colWidth,
  behavior = "auto",
}: {
  container: HTMLElement | null;
  centerIdx: number;
  colWidth: number;
  behavior?: ScrollBehavior;
}) {
  if (!container) return;
  const scrollLeft = centerIdx * colWidth - container.clientWidth / 2;
  scrollToPosition({ container, left: scrollLeft, behavior });
}

/**
 * Scrolls to the specified date within a timeline range.
 */
export function scrollToDate({
  container,
  date,
  timelineStart,
  colWidth,
  behavior = "auto",
}: {
  container: HTMLElement | null;
  date: Date;
  timelineStart: Date;
  colWidth: number;
  behavior?: ScrollBehavior;
}) {
  const start = startOfDay(timelineStart);
  const target = startOfDay(date);
  const dayIndex = differenceInCalendarDays(target, start);
  const scrollLeft = dayIndex * colWidth;
  scrollToPosition({ container, left: scrollLeft, behavior });
}

export const scrollToBooking = ({
  booking,
  scrollRef,
  timelineStart,
  visibleDays,
  behavior = "smooth",
  focusDate,
  verticalMargin = 60,
}: {
  booking: { AutoID: number; StartDate: string };
  scrollRef: RefObject<HTMLElement>;
  timelineStart: Date;
  visibleDays: number;
  behavior?: ScrollBehavior;
  focusDate?: Date | null;
  /** Extra padding above target inside the container viewport */
  verticalMargin?: number;
}) => {
  const container = scrollRef.current;
  if (!container) return;

  const startDate = focusDate ?? normalizeDateOnly(booking.StartDate);
  if (!startDate) return;

  // Horizontal target: align left to the booking's start day
  const colWidth = container.clientWidth / Math.max(1, visibleDays);
  const start = startOfDay(timelineStart);
  const target = startOfDay(startDate);
  const dayIndex = differenceInCalendarDays(target, start);
  const targetLeft = Math.max(0, dayIndex * colWidth);

  // Vertical target: compute element top relative to container and pad with margin
  let targetTop: number | undefined = undefined;
  try {
    const selector = `[data-booking-bar="${booking.AutoID}"]`;
    const el = container.querySelector(selector) as HTMLElement | null;
    if (el && typeof el.getBoundingClientRect === "function") {
      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      const rawTop = eRect.top - cRect.top + container.scrollTop;
      const baseline = Math.max(0, rawTop - Math.max(0, verticalMargin));
      const maxTop = Math.max(
        0,
        container.scrollHeight - container.clientHeight
      );
      targetTop = Math.max(0, Math.min(maxTop, baseline));
    }
  } catch {}

  try {
    container.scrollTo({
      left: targetLeft,
      top: targetTop,
      behavior,
    } as ScrollToOptions);
  } catch {
    // Fallback: call in two passes if the browser rejects combined
    scrollToPosition({ container, left: targetLeft, behavior });
    if (typeof targetTop === "number") {
      scrollToPosition({ container, top: targetTop, behavior });
    }
  }
};
